import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GalleryTraffic from './GalleryTraffic';
import DefenderEndpoint from './DefenderEndpoint';
import Cameras from './Cameras';

// Security hub — one top-level page with an internal tab bar, mirroring the
// Human Resources / Facilities hubs. Access is gated in menuModel (gate:'security')
// and defensively in App.js. Gallery Traffic (UniFi Protect people counter) is the
// first live tab; Cameras / Incidents / Access Control are scaffolds ready to build.

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

export default function Security({ currentUser }) {
  const [tab, setTab] = useState('overview');

  const tabs = [
    { key: 'overview',  label: 'Overview' },
    { key: 'endpoint',  label: 'Endpoint' },
    { key: 'gallery',   label: 'Gallery Traffic' },
    { key: 'cameras',   label: 'Cameras' },
    { key: 'incidents', label: 'Incidents' },
    { key: 'access',    label: 'Access Control' },
  ];

  const renderTab = () => {
    switch (tab) {
      case 'overview':  return <Overview onNavigate={setTab} />;
      case 'endpoint':  return <DefenderEndpoint />;
      case 'gallery':   return <GalleryTraffic />;
      case 'cameras':   return <Cameras />;
      case 'incidents': return <IncidentsScaffold />;
      case 'access':    return <AccessScaffold />;
      default:          return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '2px', padding: '0 28px', borderBottom: '1px solid #EDEDF0', background: '#fff', overflowX: 'auto' }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: '15px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '14px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#8A8A8E',
                borderBottom: active ? `2.5px solid ${ORANGE}` : '2.5px solid transparent',
                whiteSpace: 'nowrap', marginBottom: '-1px', transition: 'color .15s',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: '24px 28px' }}>{renderTab()}</div>
    </div>
  );
}

// ---- shared building blocks (match Facilities / site palette) ----
const card = { background: '#fff', border: '1px solid #EDEDF0', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const label = { fontSize: '11.5px', fontWeight: 600, color: '#8A8A8E', textTransform: 'uppercase', letterSpacing: '0.05em' };

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>{subtitle}</p>}
    </div>
  );
}

function EmptyPanel({ icon, title, note }) {
  return (
    <div style={{ ...card, padding: '56px 24px', textAlign: 'center' }}>
      <div style={{ width: 52, height: 52, borderRadius: '14px', background: '#F4F6F9', color: NAVY, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>{icon}</div>
      <div style={{ fontSize: '15px', fontWeight: 700, color: NAVY }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '6px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>{note}</div>
    </div>
  );
}

// ---- Overview: live gallery roll-up + scaffolds ----
function Overview({ onNavigate }) {
  const [gal, setGal] = useState(null); // { zones, occupancy }
  const [ep, setEp] = useState(null);   // { configured, total, healthy }
  useEffect(() => {
    axios.get(API + '/gallery-occupancy/summary', { headers: headers() })
      .then(r => {
        const z = r.data || [];
        setGal({ zones: z.length, occupancy: z.reduce((a, x) => a + (x.occupancy || 0), 0) });
      })
      .catch(() => setGal({ zones: 0, occupancy: 0 }));
    axios.get(API + '/defender/overview', { headers: headers() })
      .then(r => setEp(r.data?.configured ? { configured: true, ...r.data.summary } : { configured: false }))
      .catch(() => setEp({ configured: false }));
  }, []);

  const epCard = !ep
    ? { big: '—', sub: 'device health' }
    : !ep.configured
      ? { big: '—', sub: 'not connected' }
      : { big: `${ep.healthy}/${ep.total}`, sub: (ep.needsAttention || 0) > 0 ? `${ep.needsAttention} need attention` : 'all healthy' };

  const cards = [
    { key: 'endpoint',  title: 'Endpoint health',   big: epCard.big, sub: epCard.sub, accent: false, nav: 'endpoint' },
    { key: 'gallery',   title: 'People in galleries', big: gal ? String(gal.occupancy) : '—', sub: gal ? `${gal.zones} galleries monitored` : 'live occupancy', accent: false, nav: 'gallery' },
    { key: 'cameras',   title: 'Cameras',        big: '—', sub: 'UniFi Protect devices', accent: false, nav: 'cameras' },
    { key: 'incidents', title: 'Open incidents', big: '0', sub: 'security reports',       accent: false, nav: 'incidents' },
    { key: 'access',    title: 'Access Control', big: '—', sub: 'doors & credentials',    accent: false, nav: 'access' },
  ];
  const hover = e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.09)'; };
  const out = e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; };
  return (
    <div>
      <SectionHeader title="Security" subtitle="Monitor galleries, cameras, incidents, and building access." />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
        {cards.map(c => (
          <div key={c.key} style={{ ...card, padding: '20px 22px', cursor: 'pointer', transition: 'transform .15s, box-shadow .15s' }}
            onClick={() => onNavigate(c.nav)} onMouseEnter={hover} onMouseLeave={out}>
            <div style={{ ...label, marginBottom: '10px' }}>{c.title}</div>
            <div style={{ fontSize: '34px', fontWeight: 700, lineHeight: 1, color: c.accent ? ORANGE : NAVY }}>{c.big}</div>
            <div style={{ fontSize: '12.5px', color: '#6B7280', marginTop: '9px' }}>{c.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- scaffolds ----
function IncidentsScaffold() {
  return (
    <div>
      <SectionHeader title="Incidents" subtitle="Log and track security incidents and alarms." />
      <EmptyPanel
        icon={<svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
        title="No incidents logged"
        note="Record incidents (theft, damage, medical, alarms) with time, location, and follow-up. Build this out when ready." />
    </div>
  );
}
function AccessScaffold() {
  return (
    <div>
      <SectionHeader title="Access Control" subtitle="Doors, credentials, and access events." />
      <EmptyPanel
        icon={<svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
        title="Access control coming soon"
        note="Door schedules, credential holders, and access-card events will live here — ready to integrate with the building's access system." />
    </div>
  );
}
